import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog } from '@progress/kendo-react-dialogs';
import {
  ApplicationQuoteType,
  hasValue,
  VehicleJDPower,
  VehicleJdPower,
} from '@amsanalytics/react-shared-library';
import type {
  IJDPowerVehicle,
  IJDPowerValue,
  IJDPowerRegion,
  IJDPowerVehicleInfo,
  IVehicleJdPowerSave,
  IVehicleJdPowerExisting,
  IJDPowerPulledByInfo,
  IJDPowerPrintData,
  IJDPowerHistoryItem,
} from '@amsanalytics/react-shared-library';
import { AppState } from '../../store/rootReducer';
import { ApplicationDispatch } from '../../store/application/types';
import { fetchData } from '../../Data/fetch';
import { saveVehicleJdPower } from '../../Data/JDPowerData';
import { getEmployeeName } from '../../utils/utils';
import { updateApplicationCustomFields, validateApp } from '../../utils/appUtils';
import { showValidationDialog } from '../../utils/ValidationDialog';
import { saveApp } from '../../store/application/actions';
import { useBusinessRulesEngine } from '../../Data/BusinessRulesData';
import { useDialog } from '../../providers/DialogProvider';
import ErrorDialog from '../Dialogs/ErrorDialog';
import { ICustomValuePair } from '../../Interfaces/Application/ICustomValuePair';
import type { JDPowerButtonProps } from './types';
import {
  getCanonicalJDPowerValueType,
  getSelectedJDPowerDetail,
  normalizeJDPowerValuation,
  withCanonicalJDPowerScalars,
} from './jdPowerCanonical';
import styles from './_jd-power.module.scss';

const sumJDPowerAccessories = (items?: Array<{ value: number }>) =>
  items?.reduce((total, item) => total + item.value, 0) ?? 0;

export function JDPowerButton({ currentApp, lookups, disabled = false }: JDPowerButtonProps) {
  const dispatch = useDispatch<ApplicationDispatch>();
  const [openDialog, closeDialog] = useDialog();
  const businessRulesEngine = useBusinessRulesEngine();
  const employees = useSelector((state: AppState) => state.applications.employees);

  const [showDialog, setShowDialog] = useState(false);
  const [history, setHistory] = useState<IJDPowerHistoryItem[]>([]);

  const vehicle = currentApp.vehicle;
  const vehicleJdPower = vehicle?.vehicleJdPower;

  // Build existing value from Redux state (no extra API call needed)
  const existingValue: IVehicleJdPowerExisting | undefined =
    vehicleJdPower && hasValue(vehicleJdPower.id)
      ? {
          id: vehicleJdPower.id!,
          uid: vehicleJdPower.uid,
          regionId: vehicleJdPower.regionId,
          valueType: vehicleJdPower.valueType as 'Trade' | 'Loan' | 'Retail',
          selectedAccessoriesList: vehicleJdPower.selectedAccessoriesList,
          selectedPackagesList: vehicleJdPower.selectedPackagesList,
          pulledById: vehicleJdPower.pulledById,
          pulledByName: getEmployeeName(employees, vehicleJdPower.pulledById),
          pulledDate: vehicleJdPower.pulledDate,
        }
      : undefined;

  const pulledByInfo: IJDPowerPulledByInfo | undefined =
    vehicleJdPower && hasValue(vehicleJdPower.pulledById) && hasValue(vehicleJdPower.pulledDate)
      ? {
          name: getEmployeeName(employees, vehicleJdPower.pulledById),
          date: new Date(vehicleJdPower.pulledDate),
        }
      : undefined;

  const defaultRegionId = existingValue?.regionId;
  const defaultValueType = existingValue?.valueType as 'Trade' | 'Loan' | 'Retail' | undefined;

  // Map LookupItem[] → IJDPowerRegion[]
  const regions: IJDPowerRegion[] = lookups.jdPowerRegions.map((r) => ({
    id: r.id,
    name: r.name,
    sortOrder: r.sortOrder,
  }));

  // Map vehicle → IJDPowerVehicleInfo
  const vehicleInfo: IJDPowerVehicleInfo = {
    stockNumber: vehicle?.stockNumber ?? '',
    year: vehicle?.year ?? 0,
    make: vehicle?.make ?? '',
    model: vehicle?.model ?? '',
    vin: vehicle?.vin ?? '',
    mileage: vehicle?.mileage ?? 0,
  };

  const activeUser = useSelector((state: AppState) => state.applications.loggedInUser!.employee);

  const openErrorDialog = (errors: string[]) => {
    openDialog({
      children: (
        <ErrorDialog title="Vehicle Errors" errors={errors} closeDialog={closeDialog} />
      ),
    });
  };

  const handleOpen = useCallback(async () => {
    if (!hasValue(vehicle?.vin)) {
      openErrorDialog(['Please attach a vehicle with a VIN number']);
      return;
    }

    try {
      const historyData = await fetchData(`api/JDPower/getVehicleJdPowerHistory?vehicleId=${vehicle?.id}`, dispatch);
      if (Array.isArray(historyData)) {
        setHistory(historyData as IJDPowerHistoryItem[]);
      } else {
        setHistory([]);
      }
    } catch {
      setHistory([]);
    }

    setShowDialog(true);
  }, [vehicle?.vin, vehicle?.id, dispatch]);

  const handleClose = useCallback(() => {
    setShowDialog(false);
  }, []);

  const handleLoadModels = useCallback(
    async (regionId: string): Promise<IJDPowerVehicle[]> => {
      const url = `api/JDPower/getVehicles?applicationId=${currentApp.id}&vehicleId=${vehicle?.id}&regionId=${regionId}`;
      const result = await fetchData(url, dispatch);
      if ((result as { message: string }).message !== undefined) {
        return [];
      }
      return result as IJDPowerVehicle[];
    },
    [currentApp.id, vehicle?.id, dispatch]
  );

  const handleLoadValue = useCallback(
    async (regionId: string, uid: string): Promise<IJDPowerValue> => {
      const url = `api/JDPower/getVehicleValue?applicationId=${currentApp.id}&vehicleId=${vehicle?.id}&regionId=${regionId}&uid=${uid}`;
      const result = await fetchData(url, dispatch);
      if ((result as { message: string }).message !== undefined) {
      }
      return result as IJDPowerValue;
    },
    [currentApp.id, vehicle?.id, dispatch]
  );

  const handleSave = useCallback(
    async (value: IVehicleJdPowerSave): Promise<void> => {
      const canonicalValue = withCanonicalJDPowerScalars(value);

      // Build VehicleJdPower for the Redux thunk
      const vehicleJdPowerToSend = {
        id: canonicalValue.id,
        vehicleBaseId: canonicalValue.vehicleBaseId,
        uid: canonicalValue.uid,
        regionId: canonicalValue.regionId,
        valueType: canonicalValue.valueType,
        retail: canonicalValue.retail,
        tradeIn: canonicalValue.tradeIn,
        loan: canonicalValue.loan,
        selectedAccessoriesList: canonicalValue.selectedAccessoriesList,
        accessoryAdjustment: canonicalValue.accessoryAdjustment,
        selectedPackagesList: canonicalValue.selectedPackagesList,
        mileageAdjustment: canonicalValue.mileageAdjustment,
        bookout: canonicalValue.bookout,
        pulledById: activeUser.id,
        pulledDate: new Date(),
        selectedModelName: canonicalValue.selectedModelName,
        msrp: canonicalValue.msrp,
        tradeInDetail: canonicalValue.tradeInDetail,
        loanDetail: canonicalValue.loanDetail,
        retailDetail: canonicalValue.retailDetail,
      } as VehicleJdPower;

      const isIndirect = (currentApp.quoteType === ApplicationQuoteType.Indirect);
      await dispatch(saveVehicleJdPower(vehicleJdPowerToSend, currentApp.id, isIndirect));

      // Update JDPower Value custom field if it exists
      const jdPowerCustomField = currentApp.applicationCustomFields.find(
        (acf) => acf.customField.name === 'JDPower Value'
      );
      if (jdPowerCustomField) {
        const jdPowerCustomValuePairs: ICustomValuePair[] = [
          {
            customFieldName: 'JDPower Value',
            formattedValue: canonicalValue.bookout,
            valueType: 'calculatedValue',
          },
          {
            customFieldName: 'JDPower Value',
            formattedValue: canonicalValue.bookout,
            valueType: 'value',
          },
        ];
        updateApplicationCustomFields(jdPowerCustomValuePairs, currentApp, dispatch, businessRulesEngine);

        // Save the application to persist custom field changes
        const errors: string[] = validateApp(currentApp);
        if (errors.length === 0) {
          const result = businessRulesEngine({ action: 'Save' });
          if (result?.errors?.length) {
            showValidationDialog({
              errors: result.errors,
              callback: () => {},
            });
          } else {
            await dispatch(saveApp(businessRulesEngine, false));
          }
        }
      }
    },
    [currentApp, activeUser.id, dispatch, businessRulesEngine]
  );

  const handlePrint = useCallback((rawData: IJDPowerPrintData) => {
    const data = normalizeJDPowerValuation(rawData);

    // Get primary customer info for print
    const primaryAppCustomer = currentApp.appCustomers.find(ac => ac.customerRole.isPrimaryApplicant);
    const primaryCustomer = primaryAppCustomer?.customer;
    const primaryAddress = primaryCustomer?.customerAddress?.find(a => a.default && !a.deleted);
    const primaryPhone = primaryCustomer?.customerPhone?.find(p => p.isPreferred && !p.deleted)
      ?? primaryCustomer?.customerPhone?.find(p => !p.deleted);
    const fmtPhone = (phone?: string) => {
      if (!phone) return '\u2014';
      const digits = phone.replace(/\D/g, '');
      if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      return phone;
    };
    const fmt = (v?: number) =>
      v != null
        ? v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 })
        : '\u2014';

    const hasDetails = !!(data.tradeInDetail || data.loanDetail || data.retailDetail);
    const trade = data.tradeInDetail;
    const loan = data.loanDetail;
    const retail = data.retailDetail;
    const sel = data.valueType;
    const hl = (type: string) => (getCanonicalJDPowerValueType(type) === getCanonicalJDPowerValueType(sel) ? ' hl' : '');
    const selectedDetail = getSelectedJDPowerDetail(data);
    const selectedAccessoryAdjustment = selectedDetail?.accessoryAdjustment;
    const summaryBaseValue = selectedDetail?.baseValue ?? data.baseValue;
    const summaryMileageAdjustment = selectedDetail?.mileageAdjustment ?? data.mileageAdjustment;
    const summaryBookoutValue = selectedDetail?.bookout ?? data.bookoutValue;
    const summaryAddsTotal = selectedDetail
      ? (selectedDetail.additions?.length
        ? sumJDPowerAccessories(selectedDetail.additions)
        : (selectedAccessoryAdjustment != null && selectedAccessoryAdjustment > 0 ? selectedAccessoryAdjustment : 0))
      : data.addsTotal;
    const summaryDeductsTotal = selectedDetail
      ? (selectedDetail.deductions?.length
        ? sumJDPowerAccessories(selectedDetail.deductions)
        : (selectedAccessoryAdjustment != null && selectedAccessoryAdjustment < 0 ? Math.abs(selectedAccessoryAdjustment) : 0))
      : data.deductsTotal;

    // Merge selected accessories from all 3 detail objects by description
    const allAccessories = new Map<string, { retail: number; trade: number; loan: number }>();
    const mergeAccs = (items: Array<{ description: string; value: number }> | undefined, key: 'retail' | 'trade' | 'loan') => {
      items?.forEach((a) => {
        const existing = allAccessories.get(a.description) ?? { retail: 0, trade: 0, loan: 0 };
        existing[key] = a.value;
        allAccessories.set(a.description, existing);
      });
    };
    if (hasDetails) {
      mergeAccs([...(retail?.additions ?? []), ...(retail?.deductions ?? [])], 'retail');
      mergeAccs([...(trade?.additions ?? []), ...(trade?.deductions ?? [])], 'trade');
      mergeAccs([...(loan?.additions ?? []), ...(loan?.deductions ?? [])], 'loan');
    }

    const optionsTableHtml = allAccessories.size > 0 ? `
    <h3>Selected Options</h3>
    <table class="opts-table">
      <thead>
        <tr>
          <th>Option</th>
          <th>Add/Deduct</th>
          <th class="${hl('Trade')}">Trade</th>
          <th class="${hl('Loan')}">Loan</th>
          <th class="${hl('Retail')}">Retail</th>
        </tr>
      </thead>
      <tbody>
        ${Array.from(allAccessories.entries()).map(([desc, vals]) => {
          const anyPositive = vals.retail >= 0 || vals.trade >= 0 || vals.loan >= 0;
          return `<tr>
            <td>${desc}</td>
            <td>${anyPositive ? 'Add' : 'Deduct'}</td>
            <td class="amt${hl('Trade')}">${fmt(vals.trade)}</td>
            <td class="amt${hl('Loan')}">${fmt(vals.loan)}</td>
            <td class="amt${hl('Retail')}">${fmt(vals.retail)}</td>
          </tr>`;
        }).join('')}
      </tbody>
    </table>` : '';

    const threeColumnHtml = hasDetails ? `
    <table class="compare-table">
      <thead>
        <tr>
          <th></th>
          <th class="${hl('Trade')}">Trade</th>
          <th class="${hl('Loan')}">Loan</th>
          <th class="${hl('Retail')}">Retail</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Base</td>
          <td class="amt${hl('Trade')}">${fmt(trade?.baseValue)}</td>
          <td class="amt${hl('Loan')}">${fmt(loan?.baseValue)}</td>
          <td class="amt${hl('Retail')}">${fmt(retail?.baseValue)}</td>
        </tr>
        <tr>
          <td>Mileage Adj</td>
          <td class="amt${hl('Trade')}">${fmt(trade?.mileageAdjustment)}</td>
          <td class="amt${hl('Loan')}">${fmt(loan?.mileageAdjustment)}</td>
          <td class="amt${hl('Retail')}">${fmt(retail?.mileageAdjustment)}</td>
        </tr>
        <tr>
          <td>Options</td>
          <td class="amt${hl('Trade')}">${fmt(trade?.accessoryAdjustment)}</td>
          <td class="amt${hl('Loan')}">${fmt(loan?.accessoryAdjustment)}</td>
          <td class="amt${hl('Retail')}">${fmt(retail?.accessoryAdjustment)}</td>
        </tr>
        <tr class="bookout-row">
          <td>Adjusted Total</td>
          <td class="amt${hl('Trade')}">${fmt(trade?.bookout)}</td>
          <td class="amt${hl('Loan')}">${fmt(loan?.bookout)}</td>
          <td class="amt${hl('Retail')}">${fmt(retail?.bookout)}</td>
        </tr>
      </tbody>
    </table>
    ${optionsTableHtml}` : `
    <h3>Summary</h3>
    <table class="summary-table">
      <tr><td>Base ${data.valueType} Value</td><td class="amt">${fmt(summaryBaseValue)}</td></tr>
      ${summaryAddsTotal ? `<tr><td>Additions</td><td class="amt">${fmt(summaryAddsTotal)}</td></tr>` : ''}
      ${summaryDeductsTotal ? `<tr><td>Deductions</td><td class="amt">${fmt(summaryDeductsTotal)}</td></tr>` : ''}
      <tr><td>Mileage Adjustment</td><td class="amt">${fmt(summaryMileageAdjustment)}</td></tr>
      <tr class="bookout-row"><td>Adjusted Total</td><td class="amt">${fmt(summaryBookoutValue)}</td></tr>
    </table>`;

    const html = `<!DOCTYPE html>
<html>
<head>
  <title>JD Power - ${data.vehicle.stockNumber}</title>
  <style>
    @page { size: letter; margin: 0.5in; }
    body { font-family: -apple-system, 'Segoe UI', Roboto, sans-serif; color: #1f1f1f; margin: 0; padding: 0; font-size: 13px; line-height: 1.5; }
    h1 { font-size: 18px; margin: 0 0 12px; display: inline-block; }
    .sheet-label { float: right; font-size: 14px; color: #555; margin-top: 4px; }
    h3 { font-size: 13px; margin: 16px 0 6px; padding-bottom: 4px; border-bottom: 1px solid #ccc; }
    table { width: 100%; border-collapse: collapse; }
    td, th { padding: 4px 8px; text-align: left; vertical-align: top; }
    .amt { text-align: right; font-variant-numeric: tabular-nums; }
    .info-grid { display: flex; gap: 24px; margin-bottom: 16px; clear: both; }
    .info-col { flex: 1; display: flex; flex-direction: column; gap: 2px; }
    .info-grid .label { font-weight: 600; color: #333; }
    .compare-table th, .opts-table th { font-weight: 600; font-size: 12px; text-transform: uppercase; color: #555; border-bottom: 2px solid #333; padding: 6px 8px; text-align: right; }
    .compare-table th:first-child, .opts-table th:first-child, .opts-table th:nth-child(2) { text-align: left; }
    .compare-table td, .opts-table td { padding: 5px 8px; border-bottom: 1px solid #eee; }
    .compare-table .bookout-row td { border-top: 2px solid #333; border-bottom: none; font-weight: 700; font-size: 14px; }
    .hl { background: #f0f0f0; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .summary-table { background: #f5f5f5; border-radius: 4px; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .summary-table td { padding: 6px 8px; }
    .summary-table tr:last-child { font-weight: 700; font-size: 15px; }
    .summary-table .bookout-row td { border-top: 2px solid #333; }
    .subtle { color: #666; font-size: 11px; margin-top: 16px; }
  </style>
</head>
<body>
  <h1>JD Power Vehicle Valuation</h1>
  <span class="sheet-label">Bookout Sheet</span>

  <div class="info-grid">
    <div class="info-col">
      <div><span class="label">Date:</span> ${data.pulledByInfo ? new Date(data.pulledByInfo.date).toLocaleDateString() : new Date().toLocaleDateString()}</div>
      <div><span class="label">VIN:</span> ${data.vehicle.vin ?? '\u2014'}</div>
      <div><span class="label">Stock Number:</span> ${data.vehicle.stockNumber}</div>
      <div><span class="label">Actual Mileage:</span> ${data.vehicle.mileage?.toLocaleString() ?? '\u2014'}</div>
      <div><span class="label">Year:</span> ${data.vehicle.year}</div>
      <div><span class="label">Make:</span> ${data.vehicle.make}</div>
      <div><span class="label">Model:</span> ${data.vehicle.model}</div>
      <div><span class="label">Body Style:</span> ${vehicle?.body ?? '\u2014'}</div>
      <div><span class="label">Color:</span> ${vehicle?.color ?? '\u2014'}</div>
      <div><span class="label">MSRP:</span> ${fmt(data.msrp)}</div>
      <div><span class="label">Region:</span> ${data.regionName}</div>
    </div>
    <div class="info-col">
      <div><span class="label">Deal ID:</span> ${currentApp.applicationNo ?? '\u2014'}</div>
      <div><span class="label">Customer:</span> ${primaryCustomer ? [primaryCustomer.firstName, primaryCustomer.middleName, primaryCustomer.lastName].filter(Boolean).join(' ') : '\u2014'}</div>
      <div><span class="label">Address:</span> ${primaryAddress ? [primaryAddress.street1, primaryAddress.street2].filter(Boolean).join(', ') : '\u2014'}</div>
      <div><span class="label">City:</span> ${primaryAddress?.city ?? '\u2014'}</div>
      <div><span class="label">State:</span> ${primaryAddress?.state?.name ?? primaryAddress?.stateAbbreviation ?? '\u2014'}</div>
      <div><span class="label">ZIP:</span> ${primaryAddress?.zipCode ?? '\u2014'}</div>
      <div><span class="label">Phone:</span> ${fmtPhone(primaryPhone?.phoneNumber)}</div>
    </div>
  </div>

  ${threeColumnHtml}

  ${data.pulledByInfo ? `<p class="subtle">Pulled by ${data.pulledByInfo.name} on ${new Date(data.pulledByInfo.date).toLocaleString('en-US', { timeZone: 'UTC', month: 'numeric', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '')} UTC</p>` : ''}
</body>
</html>`;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-9999px';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument ?? iframe.contentWindow?.document;
    if (!iframeDoc) {
      document.body.removeChild(iframe);
      return;
    }

    iframeDoc.open();
    iframeDoc.write(html);
    iframeDoc.close();

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 250);
  }, [currentApp]);

  if (!vehicle) return null;

  return (
    <>
      <Button
        fillMode="flat"
        themeColor="primary"
        onClick={handleOpen}
        disabled={disabled}
        title="Get JD Power Value"
        icon="dollar"
        size="small"
      />

      {showDialog && (
        <Dialog
          title="JD Power Management"
          onClose={handleClose}
          className={styles.jdPowerDialogFullscreen}
        >
          <VehicleJDPower
            vehicle={vehicleInfo}
            vehicleBaseId={vehicle?.id ?? ''}
            regions={regions}
            currentUserId={activeUser.id}
            currentUserName={getEmployeeName(employees, activeUser.id)}
            onLoadModels={handleLoadModels}
            onLoadValue={handleLoadValue}
            onSave={handleSave}
            onClose={handleClose}
            existingValue={existingValue}
            defaultRegionId={defaultRegionId}
            defaultValueType={defaultValueType}
            pulledByInfo={pulledByInfo}
            onPrint={handlePrint}
            history={history}
          />
        </Dialog>
      )}
    </>
  );
}

export default JDPowerButton;
